import React, { useEffect, useRef, useState } from 'react';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import {
  alpha,
  Box,
  ClickAwayListener,
  IconButton,
  Tooltip,
  useTheme,
  type SxProps,
  type Theme,
} from '@mui/material';
import { renderHelpContent } from './RichTooltipContent';
import type { HelpValue } from './helpModel';
import { TooltipHoverEnterDelayMs } from './tooltipTiming';
import { useTooltipHoverLoadingIndicator } from './useTooltipHoverLoadingIndicator';

interface HelpTooltipTriggerProps {
  label: string;
  help?: HelpValue;
  placement?: 'bottom-end' | 'bottom-start' | 'top' | 'top-start' | 'right';
  iconSize?: number;
  sx?: SxProps<Theme>;
}

const HelpTooltipTrigger: React.FC<HelpTooltipTriggerProps> = ({
  label,
  help,
  placement = 'top-start',
  iconSize = 16,
  sx,
}) => {
  const theme = useTheme();
  const resolvedIconSize = Math.max(iconSize, 16);
  const [openMode, setOpenMode] = useState<'hover' | 'focus' | 'click' | null>(null);
  const hoverOpenTimerRef = useRef<number | null>(null);
  const isHoveredRef = useRef(false);
  const open = openMode !== null;
  const hoverLoading = useTooltipHoverLoadingIndicator({ active: open, enabled: Boolean(help) });

  const clearHoverOpenTimer = () => {
    if (hoverOpenTimerRef.current === null) {
      return;
    }

    window.clearTimeout(hoverOpenTimerRef.current);
    hoverOpenTimerRef.current = null;
  };

  useEffect(() => {
    return () => {
      clearHoverOpenTimer();
    };
  }, []);

  if (!help) {
    return null;
  }

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    clearHoverOpenTimer();
    hoverLoading.clear();
    setOpenMode((current) => (current === 'click' ? null : 'click'));
  };

  const handleClose = () => {
    clearHoverOpenTimer();
    hoverLoading.clear();
    setOpenMode(null);
  };

  const handleFocus = () => {
    clearHoverOpenTimer();
    hoverLoading.clear();
    setOpenMode((current) => (current === 'click' ? current : 'focus'));
  };

  const handleBlur = (event: React.FocusEvent<HTMLButtonElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }

    clearHoverOpenTimer();
    hoverLoading.clear();
    setOpenMode((current) => {
      if (current === 'click') {
        return current;
      }

      if (current === 'focus' && isHoveredRef.current) {
        return 'hover';
      }

      return current === 'hover' ? current : null;
    });
  };

  const handleMouseEnter = () => {
    isHoveredRef.current = true;
    clearHoverOpenTimer();

    if (openMode === 'click' || openMode === 'focus' || openMode === 'hover') {
      return;
    }

    hoverOpenTimerRef.current = window.setTimeout(() => {
      hoverOpenTimerRef.current = null;
      hoverLoading.clear();
      setOpenMode((current) => {
        if (current === 'click' || !isHoveredRef.current) {
          return current;
        }

        return 'hover';
      });
    }, TooltipHoverEnterDelayMs);
  };

  const handleMouseLeave = () => {
    isHoveredRef.current = false;
    clearHoverOpenTimer();
    hoverLoading.clear();
    setOpenMode((current) => (current === 'hover' ? null : current));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      clearHoverOpenTimer();
      hoverLoading.clear();
      setOpenMode(null);
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <Tooltip
          open={open}
          title={renderHelpContent(help)}
          placement={placement}
          arrow
          disableFocusListener
          disableHoverListener
          disableTouchListener
          disableInteractive
        >
          <IconButton
            type="button"
            size="small"
            aria-label={`Explain ${label}`}
            aria-expanded={open ? 'true' : 'false'}
            onClick={handleToggle}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onMouseEnter={(event) => {
              hoverLoading.onMouseEnter(event);
              handleMouseEnter();
            }}
            onMouseMove={hoverLoading.onMouseMove}
            onMouseLeave={handleMouseLeave}
            onKeyDown={handleKeyDown}
            disableRipple
            disableFocusRipple
            disableTouchRipple
            sx={{
              width: 24,
              height: 24,
              minWidth: 24,
              minHeight: 24,
              p: 0,
              borderRadius: '50%',
              // Global MuiIconButton override adds a 1px border app-wide; the
              // help glyph already has its own ring, so the button chrome must go.
              border: 'none',
              color: theme.palette.text.secondary,
              backgroundColor: 'transparent',
              transition: 'color 0.2s ease, background-color 0.2s ease',
              '&:hover, &:focus-visible': {
                color: theme.palette.primary.light,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
              ...sx,
            }}
          >
            <HelpOutlineRoundedIcon sx={{ fontSize: resolvedIconSize }} />
          </IconButton>
        </Tooltip>
        {hoverLoading.indicator}
      </Box>
    </ClickAwayListener>
  );
};

export default HelpTooltipTrigger;
